import React from 'react';
import { formatAdminUtcDate } from '../../lib/adminDates';
import { getDocumentStatePresentation } from '../../lib/legalDocumentPresentation';
import type {
  AdminDocumentPackageBundle,
  AdminDocumentPackageDetail,
} from '../../legalTypes';
import { AdminButton, StatusPill } from '../components';

const TerminalHistoryStates: Record<string, true> = {
  executed: true,
  superseded: true,
  voided: true,
  declined: true,
  archived: true,
};

export const PackageOverview: React.FC<{ detail: AdminDocumentPackageDetail }> = ({ detail }) => {
  const state = getDocumentStatePresentation(detail.state);
  return (
    <section className="admin-legal-card" aria-labelledby="package-overview-title">
      <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Immutable package metadata</span><h3 id="package-overview-title">Package overview</h3></div><StatusPill tone={state.tone === 'danger' ? 'critical' : state.tone}><span aria-hidden="true">{state.marker}</span> {state.label}</StatusPill></div>
      <span className="admin-visually-hidden">{detail.state.replace(/_/g, ' ')}</span>
      <p>{state.description}</p>
      {TerminalHistoryStates[detail.state] && <div className="admin-legal-callout"><strong>Read-only immutable history</strong><p>This package and its generated versions remain retained and cannot be edited in place.</p></div>}
      <dl className="admin-legal-detail-grid">
        <div><dt>Purpose</dt><dd>{detail.purpose.replace(/_/g, ' ')}</dd></div>
        <div><dt>Template set</dt><dd>{detail.templateSetVersion}</dd></div>
        <div><dt>Template integrity</dt><dd data-visual-mask>{detail.templateSetHash}</dd></div>
        <div><dt>Updated</dt><dd><time data-visual-mask dateTime={detail.updatedAtUtc}>{formatAdminUtcDate(detail.updatedAtUtc)}</time></dd></div>
        {detail.parentPackageId && <div><dt>Lineage</dt><dd data-visual-mask>{detail.parentPackageId}</dd></div>}
        {detail.failureCode && <div><dt>Validation / failure code</dt><dd>{detail.failureCode}</dd></div>}
      </dl>
    </section>
  );
};

interface DocumentsAndVersionsProps {
  detail: AdminDocumentPackageDetail;
  bundle: AdminDocumentPackageBundle;
  onDownloadDocument: (documentId: string) => Promise<void>;
  onDownloadPackage: () => Promise<void>;
}

export const DocumentsAndVersions: React.FC<DocumentsAndVersionsProps> = ({
  detail,
  bundle,
  onDownloadDocument,
  onDownloadPackage,
}) => (
  <section className="admin-legal-card" aria-labelledby="package-documents-title">
    <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Selected manifest · {bundle.manifest.documents.length} documents</span><h3 id="package-documents-title">Immutable versions</h3></div><AdminButton disabled={bundle.versions.documents.length === 0} onClick={() => void onDownloadPackage().catch(() => undefined)}>Download package ZIP</AdminButton></div>
    {bundle.versions.documents.length === 0 ? <p>No generated document versions are available yet.</p> : <ul className="admin-legal-version-list">{bundle.versions.documents.map((version) => {
      const manifest = bundle.manifest.documents.find((item) => item.documentId === version.id);
      return <li key={version.id}><div className="admin-legal-version-list__heading"><div><strong>{version.code}</strong><span>{version.language} · {version.audience.replace(/_/g, ' ')}</span></div><AdminButton onClick={() => void onDownloadDocument(version.id).catch(() => undefined)}>Download {version.code}</AdminButton></div><dl className="admin-legal-detail-grid"><div><dt>Artifact hash</dt><dd data-visual-mask>{version.generatedArtifactHash ?? 'Not generated'}</dd></div><div><dt>Size</dt><dd>{version.generatedArtifactSizeBytes?.toLocaleString() ?? '—'} bytes</dd></div><div><dt>Renderer</dt><dd>{version.rendererVersion}</dd></div><div><dt>Signature</dt><dd>{manifest?.signatureRequired ? 'Required' : 'Not required'}</dd></div></dl></li>;
    })}</ul>}
  </section>
);

export const GenerationStatus: React.FC<{ bundle: AdminDocumentPackageBundle }> = ({ bundle }) => (
  <section className="admin-legal-card" aria-labelledby="generation-status-title">
    <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Asynchronous generation</span><h3 id="generation-status-title">Generation job</h3></div></div>
    {!bundle.job ? <p>No generation job has been queued.</p> : <div className="admin-legal-summary-line"><StatusPill tone={bundle.job.state === 'succeeded' ? 'success' : bundle.job.state === 'dead' || bundle.job.state === 'failed' ? 'critical' : 'info'}>[{bundle.job.state === 'succeeded' ? '✓' : bundle.job.state === 'dead' ? '×' : '•'}] Generation {bundle.job.state.replace(/_/g, ' ')}</StatusPill><span>{bundle.job.attempts} attempt{bundle.job.attempts === 1 ? '' : 's'}</span>{bundle.job.failureCode && <strong>{bundle.job.failureCode}</strong>}</div>}
  </section>
);

export const PackageTimeline: React.FC<{ bundle: AdminDocumentPackageBundle }> = ({ bundle }) => {
  const events = React.useMemo(
    () => [...bundle.timeline.events].sort((left, right) => left.occurredAtUtc.localeCompare(right.occurredAtUtc)),
    [bundle.timeline.events]
  );
  return (
    <section className="admin-legal-card" aria-labelledby="package-timeline-title">
      <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Append-only audit history</span><h3 id="package-timeline-title">Timeline</h3></div></div>
      {events.length === 0 ? <p>No timeline events are available.</p> : <ol className="admin-legal-timeline">{events.map((event) => <li key={event.id}><span className="admin-legal-timeline__marker" aria-hidden="true">•</span><div><strong>{event.eventType}</strong><span>{event.actorType} · <time data-visual-mask dateTime={event.occurredAtUtc}>{formatAdminUtcDate(event.occurredAtUtc)}</time></span></div></li>)}</ol>}
    </section>
  );
};
